import { IonIcon } from "@ionic/react";
import { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { ProfileType } from "../../../../types/profile";
import { chevronForward } from "ionicons/icons";
import NextPrevButtons from "../../../molecules/NextPrevButtons";
import styled from "styled-components";
import Container from "../../../atoms/Container";
import { useHomeContext } from "../../../../pages/Home/HomeContent";
import { useConfig } from "../../../../contexts/ConfigContext";

// Brand teal / coral first, then a few extras so 3+ profiles stay visually distinct.
const CARD_COLORS = ["#00c5bd", "#f8815c", "#4285F4", "#9b59b6", "#27ae60", "#e67e22"];

const initialOf = (label?: string): string =>
  (label?.trim()?.charAt(0) ?? "?").toUpperCase();

const DisplayProfileSelector = (): ReactElement => {
  const { t } = useTranslation();

  const { goPrevious, createUser, signupSubjects } = useHomeContext();

  const { setRole } = useConfig();

  const onProfileSelection = (newRole: ProfileType): void => {
    setRole(newRole);
    // Start this role's registration flow (createUser → onboarding → consent → …).
    createUser(newRole);
  };

  return (
    <Container>
      <Content>
        <Header>
          <Title>{t("home_profile_selector_text")}</Title>
          <Subtitle>{t("home_profile_selector_subtitle")}</Subtitle>
        </Header>

        <List>
          {signupSubjects.map((role: ProfileType, index: number) => {
            const color = CARD_COLORS[index % CARD_COLORS.length];
            return (
              <Card
                key={role.id}
                $color={color}
                onClick={(): void => onProfileSelection(role)}
                role="button"
                tabIndex={0}
                onKeyDown={(e): void => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onProfileSelection(role);
                  }
                }}
              >
                <Avatar $color={color}>{initialOf(role.profile)}</Avatar>
                <ProfileName>{role.profile}</ProfileName>
                <Chevron>
                  <IonIcon icon={chevronForward} />
                </Chevron>
              </Card>
            );
          })}
        </List>
      </Content>

      <NextPrevButtons onPreviousClick={goPrevious} />
    </Container>
  );
};

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 560px;
  margin: 0 auto;
  padding: 32px 20px 8px;
  box-sizing: border-box;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 28px;
`;

const Title = styled.h1`
  color: #393939;
  font-weight: 700;
  font-size: 1.6rem;
  margin: 0 0 8px;
`;

const Subtitle = styled.p`
  color: #7b7b7b;
  font-size: 0.95rem;
  line-height: 1.4;
  margin: 0;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Card = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 16px;
  background: #ffffff;
  border: 1px solid #eef0f2;
  border-left: 5px solid ${(p) => p.$color};
  border-radius: 16px;
  padding: 16px 18px;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06);
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 22px rgba(0, 0, 0, 0.1);
  }
  &:active {
    transform: translateY(0);
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.08);
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${(p) => `${p.$color}55`};
  }
`;

const Avatar = styled.div<{ $color: string }>`
  flex: 0 0 auto;
  height: 52px;
  width: 52px;
  border-radius: 50%;
  background: ${(p) => p.$color};
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  font-weight: 700;
  box-shadow: 0 4px 10px ${(p) => `${p.$color}55`};
`;

const ProfileName = styled.span`
  flex: 1;
  min-width: 0;
  color: #2c2c2c;
  font-weight: 600;
  font-size: 1.05rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Chevron = styled.span`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  color: #c4c8cc;
  font-size: 1.3rem;
`;

export default DisplayProfileSelector;
